import React, { useEffect, useMemo, useState } from "react";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";

import Swal from "sweetalert2";

import { pdf } from "@react-pdf/renderer";
import ReportCardPDF from "./ReportCardPDF";
import ONScreenReportCard from "./ONScreenReportCard";

import {
  Container,
  CircularProgress,
  Button,
  Box,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Chip,
  Divider,
  Stack,
} from "@mui/material";

import {
  AssessmentOutlined,
  ClassOutlined,
  PersonSearchOutlined,
  PictureAsPdfOutlined,
  PrintOutlined,
  SchoolOutlined,
  DescriptionOutlined,
  InsightsOutlined,
  VerifiedOutlined,
  InfoOutlined,
  CalendarMonthOutlined,
  GroupsOutlined,
  ArrowForwardRounded,
  DownloadOutlined,
  AssignmentTurnedInOutlined,
} from "@mui/icons-material";

import JSZip from "jszip";
import { saveAs } from "file-saver";

/* ============================================================
   BRAND COLORS
============================================================ */

const COLORS = {
  deepNavy: "#031326",
  navy: "#061A33",
  royalNavy: "#0A2E5A",
  blue: "#124A8A",

  charcoal: "#111827",
  slate: "#334155",
  muted: "#64748B",

  gold: "#D4A017",
  brightGold: "#F4C542",
  softGold: "#FFF7D6",

  white: "#FFFFFF",
  paper: "#FFFFFF",
  soft: "#F5F7FA",
  softBlue: "#EDF4FB",

  border: "#D9E2EC",

  success: "#1F8A5B",
  successSoft: "#ECFDF3",

  danger: "#B42318",
  dangerSoft: "#FFF1F0",
};

/* ============================================================
   COMPONENT
============================================================ */

const ReportCard = () => {
  const { selectedYear } = useAcademicYear();

  const token = localStorage.getItem("access");

  /* ============================================================
     STATE
  ============================================================ */

  const [gradeClasses, setGradeClasses] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedGradeClass, setSelectedGradeClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [reportCard, setReportCard] = useState(null);

  const [loading, setLoading] = useState(false);

  const [showRemarksInPDF, setShowRemarksInPDF] = useState(false);

  const [promotionChecks, setPromotionChecks] = useState({
    promoted: false,
    condition: false,
    fail: false,
    neverToReturn: false,
  });

  /* ============================================================
     SEMESTER PERIODS
  ============================================================ */

  const sem1Periods = [
    "Period 1",
    "Period 2",
    "Period 3",
    "Exam",
  ];

  const sem2Periods = [
    "Period 4",
    "Period 5",
    "Period 6",
    "Exam2",
  ];

  /* ============================================================
     SUMMARY INFORMATION
  ============================================================ */

  const studentCount = students.length;

  const selectedYearLabel =
    selectedYear?.name ||
    selectedYear?.academic_year ||
    selectedYear?.year ||
    "No academic year selected";

  const selectedClassLabel =
    selectedGradeClass?.name ||
    "Select a grade class";

  const reportStatus = reportCard
    ? "Report Generated"
    : selectedStudent
      ? "Ready to Generate"
      : "Awaiting Selection";

  /* ============================================================
     FETCH GRADE CLASSES
  ============================================================ */

  useEffect(() => {
    api
      .get("/api/users/grade-classes/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setGradeClasses(response.data || []);
      })
      .catch((error) => {
        console.error(
          "Grade class fetch error:",
          error
        );

        Swal.fire(
          "Unable to Load Classes",
          "The grade classes could not be loaded. Please refresh the page and try again.",
          "error"
        );
      });
  }, [token]);

  /* ============================================================
     FETCH STUDENTS BY CLASS + YEAR
  ============================================================ */

  useEffect(() => {
    if (
      selectedGradeClass?.id &&
      selectedYear?.id
    ) {
      api
        .get(
          `/students-by-grade-class/?grade_class=${selectedGradeClass.id}&academic_year=${selectedYear.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setStudents(response.data || []);
        })
        .catch((error) => {
          console.error(
            "Student fetch error:",
            error
          );

          setStudents([]);

          Swal.fire(
            "Unable to Load Students",
            "Students could not be loaded for the selected grade class and academic year.",
            "error"
          );
        });

      setSelectedStudent(null);
      setReportCard(null);
    } else {
      setStudents([]);
      setSelectedStudent(null);
      setReportCard(null);
    }
  }, [
    selectedGradeClass,
    selectedYear,
    token,
  ]);

  /* ============================================================
     GENERATE SINGLE STUDENT REPORT CARD
  ============================================================ */

  const handleGenerateReportCard = async () => {
    if (!selectedGradeClass) {
      Swal.fire(
        "Select a Grade Class",
        "Choose the student's grade class before generating an academic report.",
        "warning"
      );

      return;
    }

    if (!selectedStudent) {
      Swal.fire(
        "Select a Student",
        "Choose a student whose academic grades and outcomes you want to review.",
        "warning"
      );

      return;
    }

    if (!selectedYear?.id) {
      Swal.fire(
        "Academic Year Required",
        "Please select an active academic year before generating the report.",
        "warning"
      );

      return;
    }

    setLoading(true);

    try {
      const response = await api.post(
        "/api/users/grades-reports/",
        {
          grade_class: selectedGradeClass.id,
          student: selectedStudent.id,
          academic_year: selectedYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log(
        "REPORT CARD RESPONSE:",
        response.data
      );

      if (
        response.data &&
        Array.isArray(response.data.period_grades)
      ) {
        setReportCard(response.data);

        Swal.fire({
          icon: "success",
          title: "Academic Report Ready",
          text: "The student's grade sheet and academic outcome report have been generated successfully.",
          confirmButtonColor: COLORS.royalNavy,
        });
      } else {
        setReportCard(null);

        Swal.fire(
          "No Grades Available",
          "No academic grades were found for this student in the selected academic year.",
          "warning"
        );
      }
    } catch (error) {
      console.error(
        "Report generation error:",
        error
      );

      Swal.fire(
        "Report Generation Failed",
        error.response?.data?.error ||
          "The academic report could not be generated.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     DOWNLOAD SINGLE STUDENT REPORT CARD
  ============================================================ */

  const handleDownloadStudentReportCard = async () => {
    if (!reportCard) {
      Swal.fire(
        "Report Required",
        "Generate the student's academic report before downloading the PDF.",
        "warning"
      );

      return;
    }

    try {
      Swal.fire({
        title: "Preparing Academic Report",
        text: "Creating the student's report card PDF. Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const pdfBlob = await pdf(
        <ReportCardPDF
          responses={[
            {
              data: reportCard,
            },
          ]}
          selectedGradeClass={selectedGradeClass}
          selectedYear={selectedYear}
          showRemarksInPDF={showRemarksInPDF}
        />
      ).toBlob();

      const cleanName = (
        reportCard.student_name ||
        selectedStudent?.full_name ||
        "Student"
      ).replace(
        /[^a-z0-9]/gi,
        "_"
      );

      saveAs(
        pdfBlob,
        `${cleanName}_ReportCard.pdf`
      );

      Swal.close();

      Swal.fire({
        icon: "success",
        title: "Download Complete",
        text: "The student's academic report card has been downloaded successfully.",
        confirmButtonColor: COLORS.royalNavy,
      });
    } catch (error) {
      console.error(
        "Student report card download error:",
        error
      );

      Swal.close();

      Swal.fire(
        "Download Failed",
        "The student report card PDF could not be generated.",
        "error"
      );
    }
  };

  /* ============================================================
     PRINT ALL CLASS REPORT CARDS
  ============================================================ */

  const handlePrintClassReportCards = async () => {
    if (!selectedGradeClass?.id) {
      Swal.fire(
        "Grade Class Required",
        "Select a grade class before generating the class report package.",
        "warning"
      );

      return;
    }

    if (!students.length) {
      Swal.fire(
        "No Students Found",
        "There are no students available for the selected grade class and academic year.",
        "warning"
      );

      return;
    }

    if (!selectedYear?.id) {
      Swal.fire(
        "Academic Year Required",
        "Please select an academic year before generating class reports.",
        "warning"
      );

      return;
    }

    setLoading(true);

    try {
      const zip = new JSZip();

      Swal.fire({
        title: "Generating Class Reports",
        text: "Preparing individual academic report cards...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      for (
        let i = 0;
        i < students.length;
        i++
      ) {
        const student = students[i];

        Swal.update({
          text: `Preparing ${student.full_name} (${i + 1}/${students.length})`,
        });

        const response = await api.post(
          "/api/users/grades-reports/",
          {
            grade_class: selectedGradeClass.id,
            student: student.id,
            academic_year: selectedYear.id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.data?.period_grades) {
          continue;
        }

        const pdfBlob = await pdf(
          <ReportCardPDF
            responses={[
              {
                data: response.data,
              },
            ]}
            selectedGradeClass={selectedGradeClass}
            selectedYear={selectedYear}
            showRemarksInPDF={showRemarksInPDF}
          />
        ).toBlob();

        const cleanName = (
          student.full_name ||
          `Student_${i + 1}`
        ).replace(
          /[^a-z0-9]/gi,
          "_"
        );

        zip.file(
          `${cleanName}_ReportCard.pdf`,
          pdfBlob
        );

        await new Promise((resolve) =>
          setTimeout(resolve, 100)
        );
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6,
        },
      });

      const className = (
        selectedGradeClass.name ||
        "Grade_Class"
      ).replace(
        /[^a-z0-9]/gi,
        "_"
      );

      saveAs(
        zipBlob,
        `${className}_ReportCards.zip`
      );

      Swal.close();

      Swal.fire({
        icon: "success",
        title: "Class Reports Ready",
        text: "All available student report cards have been packaged into a ZIP file.",
        confirmButtonColor: COLORS.royalNavy,
      });
    } catch (error) {
      console.error(
        "Class report generation error:",
        error
      );

      Swal.close();

      Swal.fire(
        "Generation Failed",
        "The class report package could not be generated completely.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     PROMOTION CHECK HANDLER
  ============================================================ */

  const handlePromotionCheckChange = (
    event
  ) => {
    const {
      name,
      checked,
    } = event.target;

    setPromotionChecks((previous) => ({
      ...previous,
      [name]: checked,
    }));
  };

  /* ============================================================
     SEARCH PLACEHOLDER
  ============================================================ */

  const studentPlaceholder = useMemo(() => {
    if (!selectedGradeClass) {
      return "Select a grade class first";
    }

    if (!students.length) {
      return "No students available";
    }

    return "Search student by name";
  }, [
    selectedGradeClass,
    students,
  ]);

  /* ============================================================
     RETURN
  ============================================================ */

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background:
          "linear-gradient(145deg, #eef2f6 0%, #f8fafc 48%, #e9eef4 100%)",
        py: {
          xs: 1.5,
          sm: 3,
          md: 4,
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: {
            xs: 1.25,
            sm: 2,
            md: 3,
          },
        }}
      >
        {/* ======================================================
            PAGE HEADER
        ====================================================== */}

        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: {
              xs: 2,
              md: 3,
            },
            border: `1px solid ${COLORS.border}`,
            background: `
              linear-gradient(
                120deg,
                ${COLORS.deepNavy} 0%,
                ${COLORS.navy} 55%,
                ${COLORS.royalNavy} 100%
              )
            `,
            color: COLORS.white,
            mb: 2,
            boxShadow:
              "0 14px 38px rgba(3,19,38,0.13)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 240,
              height: 240,
              borderRadius: "50%",
              right: -90,
              top: -130,
              background:
                "rgba(244,197,66,0.10)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 170,
              height: 170,
              borderRadius: "50%",
              left: -100,
              bottom: -110,
              background:
                "rgba(255,255,255,0.035)",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              p: {
                xs: 2.2,
                sm: 3,
                md: 3.5,
              },
            }}
          >
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                md: "center",
              }}
              spacing={2}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <AssessmentOutlined
                    sx={{
                      color: COLORS.brightGold,
                      fontSize: {
                        xs: 25,
                        md: 29,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      color: COLORS.brightGold,
                      fontSize: {
                        xs: 10,
                        sm: 11,
                      },
                      fontWeight: 900,
                      letterSpacing: "0.15em",
                      textTransform:
                        "uppercase",
                    }}
                  >
                    Academic Records
                  </Typography>
                </Stack>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: 24,
                      sm: 30,
                      md: 36,
                    },
                    lineHeight: 1.1,
                    fontWeight: 900,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Student Academic Reports
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    maxWidth: 760,
                    color:
                      "rgba(255,255,255,0.76)",
                    fontSize: {
                      xs: 11,
                      sm: 13,
                    },
                    lineHeight: 1.65,
                  }}
                >
                  Review student grades, academic performance,
                  reporting-period results, and documented
                  learning outcomes. Generate individual
                  report cards or prepare a complete report
                  package for an entire grade class.
                </Typography>
              </Box>

              <Box
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 220,
                  },
                  border:
                    "1px solid rgba(244,197,66,0.48)",
                  background:
                    "rgba(255,255,255,0.045)",
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color:
                      "rgba(255,255,255,0.62)",
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing:
                      "0.1em",
                    textTransform:
                      "uppercase",
                  }}
                >
                  Academic Year
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    fontWeight: 900,
                    fontSize: 14,
                    color: "#fff",
                  }}
                >
                  {selectedYearLabel}
                </Typography>

                <Divider
                  sx={{
                    borderColor:
                      "rgba(255,255,255,0.12)",
                    my: 1,
                  }}
                />

                <Typography
                  sx={{
                    color:
                      "rgba(255,255,255,0.62)",
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing:
                      "0.1em",
                    textTransform:
                      "uppercase",
                  }}
                >
                  Report Status
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    color: COLORS.brightGold,
                    fontSize: 13,
                    fontWeight: 900,
                  }}
                >
                  {reportStatus}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              height: 5,
              background:
                "linear-gradient(90deg, #D4A017, #F4C542, #D4A017)",
            }}
          />
        </Paper>

        {/* ======================================================
            EXPLANATION STRIP
        ====================================================== */}

        <Paper
          elevation={0}
          sx={{
            border:
              `1px solid ${COLORS.border}`,
            borderLeft:
              `4px solid ${COLORS.gold}`,
            background: "#ffffff",
            borderRadius: 1.5,
            p: {
              xs: 1.5,
              sm: 2,
            },
            mb: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
          >
            <InfoOutlined
              sx={{
                color: COLORS.royalNavy,
                mt: 0.1,
                fontSize: 21,
              }}
            />

            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: COLORS.deepNavy,
                }}
              >
                About Academic Reports
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: COLORS.muted,
                  fontSize: 11,
                  lineHeight: 1.6,
                }}
              >
                Academic reports bring together a student's
                assessed grades for the selected reporting
                period and academic year. They provide a
                structured record of performance that can
                support communication with students and
                families, academic review, progression
                decisions, and school record keeping.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* ======================================================
            SELECTION WORKSPACE
        ====================================================== */}

        <Paper
          elevation={0}
          sx={{
            border:
              `1px solid ${COLORS.border}`,
            borderRadius: {
              xs: 2,
              md: 2.5,
            },
            background: COLORS.white,
            overflow: "hidden",
            mb: 2,
            boxShadow:
              "0 8px 25px rgba(15,23,42,0.05)",
          }}
        >
          {/* Section header */}

          <Box
            sx={{
              px: {
                xs: 1.8,
                sm: 2.5,
              },
              py: 1.6,
              background:
                "linear-gradient(90deg, #F8FAFC, #FFFFFF)",
              borderBottom:
                `1px solid ${COLORS.border}`,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <DescriptionOutlined
                sx={{
                  color: COLORS.royalNavy,
                  fontSize: 20,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: COLORS.deepNavy,
                  }}
                >
                  Report Selection
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: 10,
                    mt: 0.15,
                  }}
                >
                  Choose the academic class and student
                  whose grades and learning outcomes should
                  be reviewed.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              p: {
                xs: 1.8,
                sm: 2.5,
              },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {/* Grade Class */}

              <Autocomplete
                options={gradeClasses}
                getOptionLabel={(option) =>
                  option?.name || ""
                }
                value={selectedGradeClass}
                onChange={(
                  event,
                  value
                ) => {
                  setSelectedGradeClass(
                    value
                  );
                }}
                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grade Class"
                    placeholder="Select the student's grade class"
                    helperText={
                      "The grade class determines which students are available for this academic report."
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <ClassOutlined
                            sx={{
                              color:
                                COLORS.royalNavy,
                              mr: 1,
                              ml: 0.5,
                              fontSize: 20,
                            }}
                          />

                          {
                            params.InputProps
                              .startAdornment
                          }
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: 9.5,
                        lineHeight: 1.35,
                      },
                    }}
                  />
                )}
              />

              {/* Student */}

              <Autocomplete
                options={students}
                getOptionLabel={(option) =>
                  option?.full_name || ""
                }
                value={selectedStudent}
                onChange={(
                  event,
                  value
                ) => {
                  setSelectedStudent(
                    value
                  );
                  setReportCard(null);
                }}
                disabled={!selectedGradeClass}
                isOptionEqualToValue={(
                  option,
                  value
                ) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Student"
                    placeholder={
                      studentPlaceholder
                    }
                    helperText={
                      selectedGradeClass
                        ? `${studentCount} student${studentCount === 1 ? "" : "s"} available in this class.`
                        : "Select a grade class before selecting a student."
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonSearchOutlined
                            sx={{
                              color:
                                selectedGradeClass
                                  ? COLORS
                                      .royalNavy
                                  : COLORS
                                      .muted,
                              mr: 1,
                              ml: 0.5,
                              fontSize: 20,
                            }}
                          />

                          {
                            params.InputProps
                              .startAdornment
                          }
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: 9.5,
                        lineHeight: 1.35,
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Current selection */}

            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: COLORS.muted,
                  fontSize: 10,
                  fontWeight: 800,
                }}
              >
                Current context:
              </Typography>

              <Chip
                size="small"
                icon={
                  <CalendarMonthOutlined />
                }
                label={selectedYearLabel}
                sx={{
                  background:
                    COLORS.softBlue,
                  color:
                    COLORS.royalNavy,
                  fontWeight: 800,
                  fontSize: 10,
                }}
              />

              <Chip
                size="small"
                icon={
                  <ClassOutlined />
                }
                label={selectedClassLabel}
                sx={{
                  background:
                    "#F1F5F9",
                  color:
                    COLORS.slate,
                  fontWeight: 800,
                  fontSize: 10,
                }}
              />

              {selectedStudent && (
                <Chip
                  size="small"
                  icon={
                    <PersonSearchOutlined />
                  }
                  label={
                    selectedStudent.full_name
                  }
                  sx={{
                    background:
                      COLORS.softGold,
                    color: "#765800",
                    fontWeight: 800,
                    fontSize: 10,
                  }}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* ======================================================
            REPORT ACTIONS
        ====================================================== */}

        <Paper
          elevation={0}
          sx={{
            border:
              `1px solid ${COLORS.border}`,
            borderRadius: {
              xs: 2,
              md: 2.5,
            },
            background:
              "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: {
                xs: 1.8,
                sm: 2.5,
              },
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                sm: "center",
              }}
              spacing={1.5}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <AssignmentTurnedInOutlined
                    sx={{
                      color: COLORS.gold,
                      fontSize: 21,
                    }}
                  />

                  <Typography
                    sx={{
                      color:
                        COLORS.deepNavy,
                      fontSize: 13,
                      fontWeight: 900,
                    }}
                  >
                    Report Actions
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: 10,
                    lineHeight: 1.5,
                    mt: 0.4,
                  }}
                >
                  Generate an individual report for detailed
                  student review or prepare reports for every
                  student in the selected class.
                </Typography>
              </Box>

              <Chip
                size="small"
                label={
                  reportStatus
                }
                icon={
                  <VerifiedOutlined />
                }
                sx={{
                  fontWeight: 800,
                  color:
                    reportCard
                      ? COLORS.success
                      : COLORS.royalNavy,
                  background:
                    reportCard
                      ? COLORS.successSoft
                      : COLORS.softBlue,
                }}
              />
            </Stack>

            <Divider
              sx={{
                my: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                alignItems: "center",
              }}
            >
              {/* Generate */}

              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress
                      size={17}
                      sx={{
                        color:
                          "#ffffff",
                      }}
                    />
                  ) : (
                    <AssessmentOutlined />
                  )
                }
                onClick={
                  handleGenerateReportCard
                }
                disabled={
                  !selectedStudent ||
                  !selectedGradeClass ||
                  loading
                }
                sx={{
                  minHeight: 42,
                  px: 2,
                  borderRadius: 1.25,
                  textTransform:
                    "none",
                  fontWeight: 900,
                  background:
                    COLORS.royalNavy,
                  boxShadow:
                    "0 5px 14px rgba(10,46,90,0.20)",
                  "&:hover": {
                    background:
                      COLORS.deepNavy,
                  },
                }}
              >
                {loading
                  ? "Generating..."
                  : "Generate Student Report"}
              </Button>

              {/* Download */}

              <Button
                variant="outlined"
                startIcon={
                  <DownloadOutlined />
                }
                onClick={
                  handleDownloadStudentReportCard
                }
                disabled={
                  !reportCard ||
                  loading
                }
                sx={{
                  minHeight: 42,
                  px: 2,
                  borderRadius: 1.25,
                  textTransform:
                    "none",
                  fontWeight: 850,
                  color:
                    COLORS.royalNavy,
                  borderColor:
                    COLORS.royalNavy,
                  "&:hover": {
                    borderColor:
                      COLORS.deepNavy,
                    background:
                      COLORS.softBlue,
                  },
                }}
              >
                Download PDF
              </Button>

              {/* Class reports */}

              <Button
                variant="outlined"
                startIcon={
                  <PrintOutlined />
                }
                onClick={
                  handlePrintClassReportCards
                }
                disabled={
                  !selectedGradeClass ||
                  students.length === 0 ||
                  loading
                }
                sx={{
                  minHeight: 42,
                  px: 2,
                  borderRadius: 1.25,
                  textTransform:
                    "none",
                  fontWeight: 850,
                  color:
                    COLORS.slate,
                  borderColor:
                    COLORS.border,
                  "&:hover": {
                    borderColor:
                      COLORS.royalNavy,
                    background:
                      "#F8FAFC",
                  },
                }}
              >
                Generate Class Reports
              </Button>
            </Box>

            {/* PDF options */}

            <Box
              sx={{
                mt: 2,
                p: 1.3,
                border:
                  `1px solid ${COLORS.border}`,
                background:
                  "#F8FAFC",
                borderRadius: 1.25,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={
                      showRemarksInPDF
                    }
                    onChange={(
                      event
                    ) => {
                      setShowRemarksInPDF(
                        event.target
                          .checked
                      );
                    }}
                    sx={{
                      color:
                        COLORS.royalNavy,
                      "&.Mui-checked": {
                        color:
                          COLORS.royalNavy,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 850,
                        color:
                          COLORS.deepNavy,
                      }}
                    >
                      Include actual remarks in PDF
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 9.5,
                        color:
                          COLORS.muted,
                        lineHeight: 1.4,
                      }}
                    >
                      When enabled, the generated PDF will
                      include the recorded academic remarks
                      associated with the report.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Paper>

        {/* ======================================================
            ACADEMIC OUTCOME INFORMATION
        ====================================================== */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          {/* Grade evidence */}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border:
                `1px solid ${COLORS.border}`,
              borderRadius: 2,
              background: "#ffffff",
            }}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.25,
                  background:
                    COLORS.softBlue,
                  color:
                    COLORS.royalNavy,
                }}
              >
                <InsightsOutlined />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color:
                      COLORS.deepNavy,
                  }}
                >
                  Academic Performance
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 9.5,
                    lineHeight: 1.55,
                    color:
                      COLORS.muted,
                  }}
                >
                  Grade records provide evidence of how the
                  student performed across assessed courses
                  and reporting periods.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Student outcome */}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border:
                `1px solid ${COLORS.border}`,
              borderRadius: 2,
              background: "#ffffff",
            }}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.25,
                  background:
                    COLORS.softGold,
                  color:
                    "#876500",
                }}
              >
                <SchoolOutlined />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color:
                      COLORS.deepNavy,
                  }}
                >
                  Learning Outcomes
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 9.5,
                    lineHeight: 1.55,
                    color:
                      COLORS.muted,
                  }}
                >
                  Results can help document academic progress,
                  areas requiring support, and the student's
                  overall development during the reporting
                  period.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Official record */}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border:
                `1px solid ${COLORS.border}`,
              borderRadius: 2,
              background: "#ffffff",
            }}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.25,
                  background:
                    COLORS.successSoft,
                  color:
                    COLORS.success,
                }}
              >
                <VerifiedOutlined />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 900,
                    color:
                      COLORS.deepNavy,
                  }}
                >
                  Academic Record
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 9.5,
                    lineHeight: 1.55,
                    color:
                      COLORS.muted,
                  }}
                >
                  Generated reports provide a structured
                  academic record for school documentation,
                  parent communication, and student progress
                  review.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* ======================================================
            REPORT DISPLAY
        ====================================================== */}

        {reportCard && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                mb: 1.5,
                p: {
                  xs: 1.5,
                  sm: 2,
                },
                border:
                  `1px solid ${COLORS.border}`,
                borderRadius: 2,
                background:
                  "linear-gradient(90deg, #031326, #0A2E5A)",
                color: "#ffffff",
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color:
                        COLORS.brightGold,
                      fontWeight: 900,
                      letterSpacing:
                        "0.12em",
                      textTransform:
                        "uppercase",
                    }}
                  >
                    Generated Academic Record
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize: {
                        xs: 17,
                        sm: 20,
                      },
                      fontWeight: 900,
                    }}
                  >
                    {reportCard.student_name ||
                      selectedStudent?.full_name ||
                      "Student Report"}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  icon={
                    <AssignmentTurnedInOutlined />
                  }
                  label="Report Ready"
                  sx={{
                    background:
                      "rgba(255,255,255,0.09)",
                    color: "#ffffff",
                    border:
                      "1px solid rgba(244,197,66,0.45)",
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Paper>

            <ONScreenReportCard
              reportCard={reportCard}
              promotionChecks={
                promotionChecks
              }
              handlePromotionCheckChange={
                handlePromotionCheckChange
              }
              selectedYear={
                selectedYear
              }
              selectedGradeClass={
                selectedGradeClass
              }
              students={students}
              handleDownloadStudentReportCard={
                handleDownloadStudentReportCard
              }
            />
          </Box>
        )}

        {/* ======================================================
            EMPTY STATE
        ====================================================== */}

        {!reportCard && (
          <Paper
            elevation={0}
            sx={{
              border:
                `1px dashed ${COLORS.border}`,
              borderRadius: 2.5,
              background:
                "rgba(255,255,255,0.72)",
              p: {
                xs: 3,
                sm: 4,
              },
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                background:
                  COLORS.softBlue,
                color:
                  COLORS.royalNavy,
              }}
            >
              <AssessmentOutlined
                sx={{ fontSize: 31 }}
              />
            </Box>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: {
                  xs: 16,
                  sm: 18,
                },
                fontWeight: 900,
                color:
                  COLORS.deepNavy,
              }}
            >
              Student Report Preview
            </Typography>

            <Typography
              sx={{
                mt: 0.7,
                maxWidth: 600,
                mx: "auto",
                fontSize: 10.5,
                lineHeight: 1.65,
                color:
                  COLORS.muted,
              }}
            >
              Select a grade class and student above, then
              generate the academic report to display the
              student's grades, performance summary, reporting
              period results, remarks, and academic outcome
              information here.
            </Typography>

            <Stack
              direction="row"
              spacing={0.8}
              justifyContent="center"
              alignItems="center"
              sx={{
                mt: 1.5,
                color:
                  COLORS.royalNavy,
              }}
            >
              <ArrowForwardRounded
                sx={{
                  fontSize: 17,
                }}
              />

              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                }}
              >
                Select a student to begin
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* ======================================================
            FOOTER INFORMATION
        ====================================================== */}

        <Box
          sx={{
            mt: 2.5,
            pb: 2,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              color: COLORS.muted,
              lineHeight: 1.5,
            }}
          >
            Academic reports should be reviewed together
            with the student's assessed work, teacher
            observations, and applicable school records.
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 9,
              fontWeight: 800,
              color: COLORS.royalNavy,
            }}
          >
            Powered by Polu Integrated School Management System
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ReportCard;